import { ref, computed, nextTick, onMounted, onBeforeUnmount, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useDeviceStore, useFirmwareStore } from '@/stores'
import { reportMouseSdkError } from './reportMouseSdkError'
import {
  ConnectionType,
  VersionType,
  DeviceType,
  ReportEvents,
  type BaseDevice,
  type IMousePerfCfgFeature,
  type MousePerfCapability,
  type RfBoostSwitch,
  type RfPacketType,
  type SensorMode,
  type SensorModePreset,
  type SensorModes,
  type KeyScanRate,
  type ReportRate,
} from '@rapoo/device-sdk'
import { compareVersions } from '@/utils/verify'

/**
 * 频率档位文案。
 *
 * 回报率的协议值按芯片不同（标准方案 0x08/0x04/.../0x81，PAW3311 方案 0x00-0x03），
 * 但「第几档 = 什么频率」是一致的，所以文案按档位下标取而不按协议值取：
 * 回报率取 `mousePerfCfg.reportRateSlots` 中的下标，按键扫描率的取值本身即档位下标。
 */
export const RATE_TIER_LABELS = [
  '125Hz',
  '250Hz',
  '500Hz',
  '1000Hz',
  '2000Hz',
  '4000Hz',
  '8000Hz',
] as const

/**
 * 传感器模式档位的界面文案。
 *
 * 协议值与档位含义不是固定对应关系（PAW3311 把协议值 5 用作「超核」），
 * 由 manifest 的 `mousePerf.sensorModes[].preset` 指定每个协议值走哪套文案。
 */
export const SENSOR_MODE_PRESETS: Record<
  SensorModePreset,
  { titleKey: string; subtitle: string; excerptKeys: string[] }
> = {
  frenzy: {
    titleKey: 'performance.sensorMode.frenzy.title',
    subtitle: 'Corded Gaming',
    excerptKeys: [
      'performance.sensorMode.frenzy.excerpt1',
      'performance.sensorMode.frenzy.excerpt2',
    ],
  },
  competitive: {
    titleKey: 'performance.sensorMode.competitive.title',
    subtitle: 'Corded Gaming',
    excerptKeys: [
      'performance.sensorMode.competitive.excerpt1',
      'performance.sensorMode.competitive.excerpt2',
    ],
  },
  hyperCore: {
    titleKey: 'performance.sensorMode.hyperCore.title',
    subtitle: 'Corded Gaming',
    excerptKeys: ['performance.sensorMode.hyperCore.excerpt1'],
  },
  firepower: {
    titleKey: 'performance.sensorMode.firepower.title',
    subtitle: 'High Performance',
    excerptKeys: ['performance.sensorMode.firepower.excerpt1'],
  },
  balanced: {
    titleKey: 'performance.sensorMode.balanced.title',
    subtitle: 'Low Power',
    excerptKeys: ['performance.sensorMode.balanced.excerpt1'],
  },
  office: {
    titleKey: 'performance.sensorMode.office.title',
    subtitle: 'Office',
    excerptKeys: ['performance.sensorMode.office.excerpt1'],
  },
}

/** 从 manifest 读取鼠标性能能力；未声明该能力的设备返回 undefined。 */
export function getMousePerfConfig(
  device: BaseDevice | null | undefined,
): MousePerfCapability['config'] | undefined {
  return device?.manifest.capabilities.find(
    (item): item is MousePerfCapability => item.type === 'mousePerf',
  )?.config
}

/**
 * 取回报率对应的传感器模式硬件槽位下标。
 *
 * 槽位表由芯片实现提供，不同芯片的协议值编号不同，调用方不需要知道设备用的是哪种传感器。
 */
export function getReportRateSlot(
  feature: IMousePerfCfgFeature | null | undefined,
  rate: ReportRate | undefined,
): number {
  if (!feature || rate === undefined) return 0
  const slot = feature.reportRateSlots.indexOf(rate)
  return slot >= 0 ? slot : 0
}

/**
 * 通讯协议（多合一 / 1T1R）能否切换。
 *
 * 静态条件由 manifest 的 `mousePerf.rfPacketType` 声明，连接方式和接收器版本是运行时条件。
 */
function isRfPacketTypeEnabled(
  cfg: MousePerfCapability['config'] | undefined,
  device: BaseDevice | null | undefined,
  receiverVersion: string | undefined,
): boolean {
  const rf = cfg?.rfPacketType
  if (!rf) return false
  if (rf.wirelessOnly && device?.getConnectionType() !== ConnectionType.Wireless) return false
  if (rf.minReceiverVersion) {
    if (!receiverVersion) return false
    if (compareVersions(receiverVersion, rf.minReceiverVersion) !== 1) return false
  }
  return true
}

interface PerfSnapshot {
  rfBoostSwitch: RfBoostSwitch
  rfPacketType: RfPacketType
  sensorMode: SensorMode
  sensorModeList: SensorModes
  keyScanRate: KeyScanRate
  reportRate: ReportRate
}

type PerfPhase = 'cold' | 'ready' | 'error'

/** 本次连接内的性能快照。页面销毁后仍保留，同一设备再次进入时先画这份数据。 */
const perfSnapshots = new Map<string, PerfSnapshot>()

function cloneSnapshot(snap: PerfSnapshot): PerfSnapshot {
  return {
    ...snap,
    sensorModeList: [...snap.sensorModeList],
  }
}

function sameModeList(left: SensorModes, right: SensorModes) {
  return left.length === right.length && left.every((mode, index) => mode === right[index])
}

function sameSnapshot(left: PerfSnapshot, right: PerfSnapshot) {
  return (
    left.rfBoostSwitch === right.rfBoostSwitch &&
    left.rfPacketType === right.rfPacketType &&
    left.sensorMode === right.sensorMode &&
    left.keyScanRate === right.keyScanRate &&
    left.reportRate === right.reportRate &&
    sameModeList(left.sensorModeList, right.sensorModeList)
  )
}

/**
 * 回读结果和等待期间的本地变更合并。
 * 上报、用户操作已经改过的字段保留本地值，其余字段用这次读到的值。
 */
function mergeFresh(
  baseline: PerfSnapshot,
  fetched: PerfSnapshot,
  current: PerfSnapshot,
): PerfSnapshot {
  return {
    rfBoostSwitch:
      current.rfBoostSwitch !== baseline.rfBoostSwitch
        ? current.rfBoostSwitch
        : fetched.rfBoostSwitch,
    rfPacketType:
      current.rfPacketType !== baseline.rfPacketType ? current.rfPacketType : fetched.rfPacketType,
    sensorMode:
      current.sensorMode !== baseline.sensorMode ? current.sensorMode : fetched.sensorMode,
    keyScanRate:
      current.keyScanRate !== baseline.keyScanRate ? current.keyScanRate : fetched.keyScanRate,
    reportRate:
      current.reportRate !== baseline.reportRate ? current.reportRate : fetched.reportRate,
    sensorModeList: sameModeList(current.sensorModeList, baseline.sensorModeList)
      ? fetched.sensorModeList
      : current.sensorModeList,
  }
}

export function usePerformance() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const firmwareStore = useFirmwareStore()
  const { currentVersions } = storeToRefs(firmwareStore)

  // ===================== 响应式状态 =====================
  const rfBoostSwitch = ref<RfBoostSwitch>(0)
  const rfPacketType = ref<RfPacketType>(0)
  const sensorMode = ref<SensorMode>(0)
  const sensorModeList = ref<SensorModes>([])
  const keyScanRate = ref<KeyScanRate>(3)
  const reportRate = ref<ReportRate>(0x82)
  const loading = ref(false)
  /** 冷启动还在读：出骨架。已有快照时保持 ready，回读不再把它打回骨架。 */
  const phase = shallowRef<PerfPhase>('cold')
  const isReading = shallowRef(false)
  /** 回读和首次挂载期间为 false，开关和色块只在这之后的操作、上报里过渡。 */
  const animateValueChanges = shallowRef(false)

  let initRequestId = 0
  let listeningDevice: BaseDevice | null = null

  // ===================== 计算属性 =====================

  /** 当前设备的性能能力声明 */
  const perfConfig = computed(() => getMousePerfConfig(currentDevice.value))

  /** 接收器主控版本，用于判断通讯协议切换是否开放 */
  const receiverVersion = computed(
    () =>
      currentVersions.value?.find(
        (v) => v.versionType === VersionType.MasterControl && v.deviceType === DeviceType.Receiver,
      )?.version,
  )

  /** 通讯协议是否可切换 */
  const enabledRfPacketType = computed(() =>
    isRfPacketTypeEnabled(perfConfig.value, currentDevice.value, receiverVersion.value),
  )

  /** 当前回报率下可用的传感器模式 */
  const availableSensorModes = computed<SensorMode[]>(
    () => perfConfig.value?.reportRateModes[reportRate.value] ?? [],
  )

  /** 判断某个传感器模式是否被禁用 */
  function isSensorModeDisabled(mode: SensorMode): boolean {
    return !availableSensorModes.value.includes(mode)
  }

  // ===================== 获取 feature 引用 =====================
  function getPerfCfgFeature() {
    const feature = currentDevice.value?.features.mousePerfCfg
    if (!feature) {
      throw new Error("Current device doesn't have mousePerfCfg feature")
    }
    return feature
  }

  function readCurrent(): PerfSnapshot {
    return {
      rfBoostSwitch: rfBoostSwitch.value,
      rfPacketType: rfPacketType.value,
      sensorMode: sensorMode.value,
      sensorModeList: [...sensorModeList.value],
      keyScanRate: keyScanRate.value,
      reportRate: reportRate.value,
    }
  }

  function applySnapshot(snap: PerfSnapshot) {
    rfBoostSwitch.value = snap.rfBoostSwitch
    rfPacketType.value = snap.rfPacketType
    sensorMode.value = snap.sensorMode
    sensorModeList.value = [...snap.sensorModeList]
    keyScanRate.value = snap.keyScanRate
    reportRate.value = snap.reportRate
  }

  function rememberCurrent() {
    const device = currentDevice.value
    if (!device) return
    perfSnapshots.set(device.id, cloneSnapshot(readCurrent()))
  }

  /** 有快照时在第一次渲染前灌进界面。内容没变时不重复赋值，避免开关再走一遍过渡。 */
  function hydrate(deviceId: string) {
    const snap = perfSnapshots.get(deviceId)
    if (!snap) return false

    const current = readCurrent()
    if (phase.value !== 'ready' || !sameSnapshot(current, snap)) {
      animateValueChanges.value = false
      applySnapshot(snap)
    }

    phase.value = 'ready'
    loading.value = false
    return true
  }

  async function commitDisplay(deviceId: string, snap: PerfSnapshot, isCurrent: () => boolean) {
    if (!isCurrent()) return

    perfSnapshots.set(deviceId, cloneSnapshot(snap))
    const changed = !sameSnapshot(readCurrent(), snap)
    phase.value = 'ready'
    loading.value = false

    if (!changed) {
      animateValueChanges.value = true
      return
    }

    animateValueChanges.value = false
    applySnapshot(snap)
    await nextTick()
    if (!isCurrent()) return
    animateValueChanges.value = true
  }

  // ===================== SDK 写入 =====================

  async function setRfBoostSwitch(value: RfBoostSwitch) {
    try {
      const feature = getPerfCfgFeature()
      await feature.setWirelessRadioPolicy(value)
      rfBoostSwitch.value = value
      rememberCurrent()
    } catch (err) {
      console.error('Failed to set RF boost switch', err)
      reportMouseSdkError('Failed to set RF boost switch', err)
    }
  }

  async function setRfPacketType(value: RfPacketType) {
    try {
      const feature = getPerfCfgFeature()
      await feature.setRFProtocol(value)
      rfPacketType.value = value
      rememberCurrent()
    } catch (err) {
      console.error('Failed to set RF packet type', err)
      reportMouseSdkError('Failed to set RF packet type', err)
    }
  }

  async function setSensorMode(value: SensorMode) {
    try {
      const feature = getPerfCfgFeature()
      await feature.setSensorMode(getReportRateSlot(feature, reportRate.value), value)
      sensorMode.value = value
      rememberCurrent()
    } catch (err) {
      console.error('Failed to set sensor mode', err)
      reportMouseSdkError('Failed to set sensor mode', err)
    }
  }

  async function setKeyScanRate(value: KeyScanRate) {
    try {
      const feature = getPerfCfgFeature()
      await feature.setScanRate(value)
      keyScanRate.value = value
      rememberCurrent()
    } catch (err) {
      console.error('Failed to set key scan rate', err)
      reportMouseSdkError('Failed to set key scan rate', err)
    }
  }

  async function setReportRate(value: ReportRate) {
    try {
      const feature = getPerfCfgFeature()
      await feature.setReportRate(value)
      reportRate.value = value
      rememberCurrent()

      // 回报率变更后，先尝试查询该回报率对应的 sensorMode
      try {
        const mode = await feature.getSensorMode(getReportRateSlot(feature, value))
        // 查询成功且该模式在当前回报率下可用，直接采用
        if (!isSensorModeDisabled(mode)) {
          sensorMode.value = mode
          rememberCurrent()
          return
        }
      } catch {
        // 查询失败（硬件不支持该指令），走 fallback
      }

      // 查询失败或得到的 mode 不可用，取当前回报率下第一个可用的模式
      const fallback = availableSensorModes.value[0]
      if (fallback !== undefined) {
        await setSensorMode(fallback)
      }
    } catch (err) {
      console.error('Failed to set report rate', err)
      reportMouseSdkError('Failed to set report rate', err)
    }
  }

  // ===================== 初始化 =====================

  async function refresh() {
    const requestId = ++initRequestId
    const device = currentDevice.value
    const feature = device?.features.mousePerfCfg
    const isCurrent = () => requestId === initRequestId && device === currentDevice.value

    try {
      if (!device || !feature) {
        phase.value = 'cold'
        loading.value = false
        isReading.value = false
        animateValueChanges.value = false
        return
      }

      const hasSnapshot = hydrate(device.id)
      if (!hasSnapshot) {
        phase.value = 'cold'
        isReading.value = true
        loading.value = true
        animateValueChanges.value = false
      } else if (!animateValueChanges.value) {
        await nextTick()
        if (!isCurrent()) return
        animateValueChanges.value = true
      }

      if (!isCurrent()) return

      const baseline = readCurrent()
      const fetched = cloneSnapshot(baseline)
      let anySuccess = false

      // 控件还没显示时，先把回报率落到该型号真实开放的档位上，避免用默认值算出空的可选模式。
      if (phase.value !== 'ready') {
        const rates = perfConfig.value?.reportRates
        if (rates?.length && !rates.includes(fetched.reportRate)) {
          fetched.reportRate = rates[rates.length - 1]
        }
      }

      try {
        const cfg = await feature.getAllPerfCfg()
        if (!isCurrent()) return
        if (cfg.rfPacketType !== undefined) fetched.rfPacketType = cfg.rfPacketType
        if (cfg.reportRate !== undefined) fetched.reportRate = cfg.reportRate
        if (cfg.keyScanRate !== undefined) fetched.keyScanRate = cfg.keyScanRate
        if (cfg.rfBoostSwitch !== undefined) fetched.rfBoostSwitch = cfg.rfBoostSwitch
        anySuccess = true
      } catch (err) {
        console.error('Failed to get all perf cfg', err)
        reportMouseSdkError('Failed to get all perf cfg', err)
      }

      if (!isCurrent()) return

      try {
        fetched.sensorModeList = await feature.getSensorModeList()
        anySuccess = true
      } catch (err) {
        console.error('Failed to get sensor mode list', err)
        reportMouseSdkError('Failed to get sensor mode list', err)
      }

      if (!isCurrent()) return

      try {
        fetched.sensorMode = await feature.getSensorMode(
          getReportRateSlot(feature, fetched.reportRate),
        )
        anySuccess = true
      } catch (err) {
        console.error('Failed to get sensor mode', err)
        reportMouseSdkError('Failed to get sensor mode', err)
      }

      if (!isCurrent()) return

      if (!anySuccess) {
        if (phase.value !== 'ready') phase.value = 'error'
        return
      }

      await commitDisplay(device.id, mergeFresh(baseline, fetched, readCurrent()), isCurrent)
    } finally {
      if (requestId === initRequestId) {
        loading.value = false
        isReading.value = false
      }
    }
  }

  // ===================== 硬件上报监听 =====================

  /** 回报率上报回调：直接更新本地 ref，避免走 SDK 读（缓存可能已失效但尚未重新读） */
  const onReportRateChanged = (config: { reportRate: ReportRate; sensorMode: SensorMode }) => {
    reportRate.value = config.reportRate
    sensorMode.value = config.sensorMode
    rememberCurrent()
  }

  /** 按键扫描率上报回调 */
  const onScanRateChanged = (rate: KeyScanRate) => {
    keyScanRate.value = rate
    rememberCurrent()
  }

  const startListenReport = () => {
    const device = currentDevice.value
    if (!device || listeningDevice === device) return

    listeningDevice?.off(ReportEvents.ReportRateChanged, onReportRateChanged)
    listeningDevice?.off(ReportEvents.ScanRateChanged, onScanRateChanged)
    device.on(ReportEvents.ReportRateChanged, onReportRateChanged)
    device.on(ReportEvents.ScanRateChanged, onScanRateChanged)
    listeningDevice = device
  }

  const stopListenReport = () => {
    listeningDevice?.off(ReportEvents.ReportRateChanged, onReportRateChanged)
    listeningDevice?.off(ReportEvents.ScanRateChanged, onScanRateChanged)
    listeningDevice = null
  }

  if (currentDevice.value?.features.mousePerfCfg) {
    const restored = hydrate(currentDevice.value.id)
    if (!restored) {
      isReading.value = true
      loading.value = true
    }
  }

  onMounted(() => {
    startListenReport()
    void refresh()
  })

  onBeforeUnmount(() => {
    initRequestId++
    stopListenReport()
  })

  watch(currentDevice, () => {
    startListenReport()
    void refresh()
  })

  return {
    // 状态
    enabledRfPacketType,
    rfBoostSwitch,
    rfPacketType,
    sensorMode,
    sensorModeList,
    keyScanRate,
    reportRate,
    loading,
    phase,
    isReading,
    animateValueChanges,

    // 计算属性
    perfConfig,
    availableSensorModes,

    // 方法
    isSensorModeDisabled,
    setRfBoostSwitch,
    setRfPacketType,
    setSensorMode,
    setKeyScanRate,
    setReportRate,
  }
}
